import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Button,
  Space,
  Descriptions,
  Tag,
  message,
  Popconfirm,
  Alert,
} from 'antd';
import {
  ArrowLeftOutlined,
  EditOutlined,
  DeleteOutlined,
  PrinterOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import apiService from '../../services/api';
import { formatCurrency, formatDate } from '../../utils/helpers';

const PaymentDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [payment, setPayment] = useState(null);
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPaymentDetails();
    // eslint-disable-next-line
  }, [id]);

  const fetchPaymentDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.payments.getById(id);
      console.log('Payment response:', response);

      const paymentData = response.data;
      if (!paymentData) {
        throw new Error('Payment not found');
      }

      setPayment(paymentData);
      // Set job and customer from the new response structure
      if (paymentData.job) {
        setJob(paymentData.job);
      }
    } catch (error) {
      console.error('Error fetching payment details:', error);
      setError(error.response?.data?.message || error.message || 'Failed to load payment details');
      message.error('Failed to load payment details');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await apiService.payments.delete(id);
      message.success('Payment deleted successfully');
      navigate('/payments');
    } catch (error) {
      console.error('Error deleting payment:', error);
      message.error('Failed to delete payment');
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const generateInvoice = () => {
    message.info('Invoice generation feature coming soon!');
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'paid':
        return 'success';
      case 'pending':
        return 'warning';
      case 'overdue':
        return 'error';
      case 'cancelled':
        return 'default';
      default:
        return 'default';
    }
  };

  if (loading) {
    return <Card loading={true} />;
  }

  if (!payment) {
    return (
      <Alert
        message="Payment Not Found"
        description="The payment you're looking for doesn't exist or has been removed."
        type="error"
        showIcon
        action={
          <Button type="primary" onClick={() => navigate('/payments')}>
            Back to Payments
          </Button>
        }
      />
    );
  }

  return (
    <div>
      {error && (
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <Card
        title={
          <Space>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate('/payments')}
            >
              Back
            </Button>
            <span>Payment Details: {payment.reference}</span>
          </Space>
        }
        extra={
          <Space>
            <Button
              icon={<PrinterOutlined />}
              onClick={handlePrint}
            >
              Print
            </Button>
            <Button
              icon={<FileTextOutlined />}
              onClick={generateInvoice}
            >
              Invoice
            </Button>
            <Button
              type="primary"
              icon={<EditOutlined />}
              onClick={() => navigate(`/payments/edit/${payment._id}`)}
            >
              Edit
            </Button>
            <Popconfirm
              title="Are you sure you want to delete this payment?"
              onConfirm={handleDelete}
              okText="Yes"
              cancelText="No"
              disabled={payment.status === 'paid'}
            >
              <Button
                danger
                icon={<DeleteOutlined />}
                disabled={payment.status === 'paid'}
              >
                Delete
              </Button>
            </Popconfirm>
          </Space>
        }
      >
        <Descriptions bordered column={2}>
          <Descriptions.Item label="Reference">{payment.reference}</Descriptions.Item>
          {job && (
            <Descriptions.Item label="Related Job">
              <Button type="link" onClick={() => navigate(`/jobs/${job._id}`)}>
                {job.bookingReference || 'View Job Details'}
              </Button>
            </Descriptions.Item>
          )}
          <Descriptions.Item label="Category">{payment.category}</Descriptions.Item>
          <Descriptions.Item label="PL Position">
            <Tag color={payment.plPosition === 'income' ? 'green' : 'volcano'}>{payment.plPosition}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Status">
            <Tag color={getStatusColor(payment.status)}>{payment.status?.toUpperCase()}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Net Amount">{formatCurrency(payment.amount)}</Descriptions.Item>
          <Descriptions.Item label="VAT Rate">{payment.vatRate}%</Descriptions.Item>
          <Descriptions.Item label="VAT Amount">{formatCurrency(payment.vatAmount)}</Descriptions.Item>
          <Descriptions.Item label="Total Amount">{formatCurrency(payment.totalAmount)}</Descriptions.Item>
          <Descriptions.Item label="Due Date">{formatDate(payment.dueDate, 'DD/MM/YYYY', true)}</Descriptions.Item>
          <Descriptions.Item label="Payment Method">{payment.paymentMethod}</Descriptions.Item>
          <Descriptions.Item label="Related Driver">{payment.relatedDriver?.name || '-'}</Descriptions.Item>
          <Descriptions.Item label="Related Van">{payment.relatedVan?.registration || '-'}</Descriptions.Item>
          <Descriptions.Item label="Receipt">
            {payment.receipt ? <a href={payment.receipt} target="_blank" rel="noopener noreferrer">View Receipt</a> : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Notes" span={2}>{payment.notes || '-'}</Descriptions.Item>
        </Descriptions>
      </Card>
    </div>
  );
};

export default PaymentDetail; 